import { originalSourceRegistry } from '../../data/oem-source-registry';
import { useEffect, useState } from 'react';
import { oemSourceDocumentSchema } from '@sqi/shared-schemas';
import { runtimeMode, type OemContext } from '../../lib/auth';
import {
  importInventory,
  importCommand,
  type ImportInventory,
  type OemWorkspace,
} from '../../lib/oem-client';
type SourceDocument = ReturnType<typeof oemSourceDocumentSchema.parse>;
export function SourceStaging({
  context,
  workspace,
}: {
  readonly context: OemContext;
  readonly workspace: OemWorkspace;
}) {
  const [raw, setRaw] = useState(''),
    [source, setSource] = useState<SourceDocument | null>(null),
    [hash, setHash] = useState(''),
    [mapping, setMapping] = useState<
      Record<string, { canonicalId: string | null; reason: string }>
    >({}),
    [inventory, setInventory] = useState<ImportInventory>({ batches: [] }),
    [comment, setComment] = useState(''),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  async function load() {
    if (runtimeMode !== 'connected') return;
    try {
      setInventory(await importInventory(context.projectId));
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'unavailable');
    }
  }
  useEffect(() => {
    if (runtimeMode === 'connected')
      void importInventory(context.projectId)
        .then(setInventory)
        .catch((e: unknown) => setMessage(e instanceof Error ? e.message : 'unavailable'));
  }, [context.projectId]);
  async function file(value: File) {
    setSource(null);
    try {
      if (value.size > 1000000) throw Error('source_size_exceeded');
      const bytes = await value.arrayBuffer(),
        text = new TextDecoder('utf8', { fatal: true }).decode(bytes);
      const parsed = oemSourceDocumentSchema.parse(JSON.parse(text));
      setRaw(text);
      setSource(parsed);
      setHash(
        Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
          .map((v) => v.toString(16).padStart(2, '0'))
          .join(''),
      );
      setMapping({});
      setMessage('รอ mapping และ dry run ไม่มีรายการใดถูกนำไปใช้');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'source_contract_invalid');
    }
  }
  async function command(action: 'stage' | 'review' | 'adopt', batchId?: string) {
    setBusy(true);
    try {
      const result = await importCommand(
        action,
        action === 'stage'
          ? {
              projectId: context.projectId,
              idempotencyKey: crypto.randomUUID(),
              artifactBytes: raw,
              transformationVersion: 'historical-pr-normalizer-v2',
              mappings: source?.sheets.flatMap((s) =>
                s.rows.map((row) => ({
                  sourceIdentity: row.sourceIdentity,
                  ...(mapping[row.sourceIdentity] ?? {
                    canonicalId: null,
                    reason: 'ยังไม่มี canonical mapping',
                  }),
                })),
              ),
            }
          : { projectId: context.projectId, batchId, idempotencyKey: crypto.randomUUID(), comment },
      );
      setMessage(`${result.state} · receipt ${result.receiptId}`);
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'import_unavailable');
    } finally {
      setBusy(false);
    }
  }
  const allowed = ['purchasing', 'admin', 'coordinator'].includes(context.role);
  return (
    <>
      <div className="page-title">
        <div>
          <details>
            <summary>ทะเบียนต้นฉบับที่คงไว้ · reference baseline</summary>
            <p>
              ต้นฉบับไม่ได้ถูกนำเข้าเป็น master ปัจจุบัน การ adopt ใน local release นี้รับเฉพาะ
              normalized fixture สังเคราะห์ที่ตรวจแล้ว
            </p>
            {originalSourceRegistry.artifacts.map((a) => (
              <p className="wrap" key={a.name}>
                {a.name} · {a.version} · {a.bytes} bytes
                <br />
                <small>SHA-256 {a.sha256}</small>
              </p>
            ))}
            <p>
              ยังไม่ยืนยัน: {originalSourceRegistry.unavailable.join(' · ')} · Library ID
              ไม่ใช่ผลตรวจ live Library
            </p>
            <p>
              Agent Office ownership: read-only adapter provenance · ยังไม่ยืนยันข้อมูลหรือ
              readiness จริง
            </p>
          </details>
          <div className="eyebrow">HISTORICAL SOURCE STAGING</div>
          <h1>ประวัติคำขอที่มีแหล่งอ้างอิง</h1>
          <p>
            บันทึกเป็นประวัติหลังทบทวน ไม่มี stock, receipt, reservation, consumption หรือ
            production BOM
          </p>
        </div>
      </div>
      <section className="card">
        <h2>แหล่งข้อมูลและ mapping ก่อนใช้</h2>
        <p>
          รับ normalized JSON ที่คง artifact/version/sheet/row จาก adapter ข้อมูลต้นฉบับยังคงเดิม
          ในการทดสอบนี้ใช้ข้อมูลสังเคราะห์เท่านั้น
        </p>
        <label>
          Normalized source JSON
          <input
            type="file"
            accept="application/json,.json"
            disabled={busy || runtimeMode !== 'connected' || !allowed}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void file(f);
            }}
          />
        </label>
        {source ? (
          <>
            <p>
              {source.artifactId} · {source.version} · {new TextEncoder().encode(raw).byteLength}{' '}
              bytes
            </p>
            <p className="version">SHA256 {hash} · historical-pr-normalizer-v2</p>
            {source.sheets.map((s) => (
              <section key={s.name}>
                <h3>{s.name}</h3>
                {s.rows.map((r) => (
                  <article key={r.sourceIdentity} className="scope">
                    <strong>
                      แถว {r.rowId} · {r.requestCode}
                    </strong>
                    <p>
                      {r.itemDescription} · {r.quantity} {r.unit}
                    </p>
                    <p>Source identity: {r.sourceIdentity}</p>
                    <label>
                      Canonical master
                      <select
                        value={mapping[r.sourceIdentity]?.canonicalId ?? ''}
                        onChange={(e) =>
                          setMapping({
                            ...mapping,
                            [r.sourceIdentity]: {
                              canonicalId: e.target.value || null,
                              reason:
                                mapping[r.sourceIdentity]?.reason ?? 'ระบุ mapping สำหรับทบทวน',
                            },
                          })
                        }
                      >
                        <option value="">ยังไม่ทราบ — คง pending</option>
                        {workspace.records
                          .filter((x) => ['material', 'component'].includes(x.kind))
                          .map((x) => (
                            <option key={x.id} value={x.id}>
                              {String(x.content.label)} v{x.version}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label>
                      เหตุผล / หลักฐาน mapping
                      <input
                        value={mapping[r.sourceIdentity]?.reason ?? ''}
                        onChange={(e) =>
                          setMapping({
                            ...mapping,
                            [r.sourceIdentity]: {
                              canonicalId: mapping[r.sourceIdentity]?.canonicalId ?? null,
                              reason: e.target.value,
                            },
                          })
                        }
                      />
                    </label>
                  </article>
                ))}
              </section>
            ))}
            <button
              className="action"
              disabled={busy || !navigator.onLine}
              onClick={() => void command('stage')}
            >
              สร้าง dry run ที่ตรึงแหล่งข้อมูล
            </button>
          </>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
      </section>
      <section className="card">
        <h2>ทบทวนและรับเฉพาะแถวที่ผ่านเกณฑ์</h2>
        <label>
          ความเห็นสำหรับ batch
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        {inventory.batches.map((b) => (
          <article key={b.id} className="scope">
            <h3>
              {b.artifactId} · {b.sourceVersion}
            </h3>
            <p className="version">
              {b.sourceHash} · {b.transformationVersion}
            </p>
            <p>
              {b.rows.length} แถว · รับเป็นประวัติ {b.adoptedRows} แถว ·{' '}
              {b.reviewed ? 'ทบทวนแล้ว' : 'รอผู้ทบทวน'}
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>แหล่ง / แถว</th>
                  <th>สถานะ</th>
                  <th>เหตุผล</th>
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.sheet} / {r.rowId}
                      <br />
                      {r.sourceIdentity}
                    </td>
                    <td>{r.disposition}</td>
                    <td>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="actions">
              <button
                className="action secondary"
                disabled={
                  busy || !navigator.onLine || !comment || context.role !== 'owner' || b.reviewed
                }
                onClick={() => void command('review', b.id)}
              >
                ทบทวนแถว eligible
              </button>
              <button
                className="action"
                disabled={
                  busy ||
                  !navigator.onLine ||
                  !comment ||
                  !allowed ||
                  !b.reviewed ||
                  b.adoptedRows > 0
                }
                onClick={() => void command('adopt', b.id)}
              >
                รับแถว eligible เป็นประวัติ
              </button>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}
